import React, { FC, Suspense, useEffect } from 'react';
import { AxiosError } from 'axios';
import { defineMessage, defineMessages, useIntl } from 'react-intl';
import { Navigate, Outlet, Route, Routes } from 'react-router-dom';
import { useQueryClient } from 'react-query';
import { useSnackbar } from 'notistack';

import { Routes as RouteDefs } from './hooks/useRoutes';
import CheckInPlanManagementDialog from './components/CheckInPlanManagement/CheckInPlanManagementDialog';
import EditWorkOrderDialog from './components/WorkOrder/EditWorkOrderDialog';
import FetchIndicator from './components/FetchIndicator';
import Guide from './components/Guide/Guide';
import SplashMessage from './components/auth/SplashMessage';
import Permissions from './components/auth/Permissions';
import RecurringWOPlanManagementDialog from './components/RecurringWorkOrderPlan/RecurringWOPlanManagementDialog';
import RequireLogin from './components/auth/RequireLogin';
import SchemaManagementDialog from './components/SchemaManagement';
import SearchResults from './components/SearchResults';
import TagManagementDialog from './components/Tags/TagManagementDialog';
import ContractorManagementDialog from './components/UserManagement/ContractorManagementDialog';
import TopBar from './components/TopBar/TopBar';
import { isRoadAuthorityUnmatchedError } from './helpers/road_authority_helpers';
import { getTenantFeatures } from './helpers/tenant_features';
import UserProfile from './components/auth/UserProfile';
import WorkOrderInstructionManager from './components/WorkOrder/WorkOrderInstructionManager';
import FeatureLicenseProvider from './providers/FeatureLicenseProvider';
import LoadingScreen from './components/LoadingScreen';
import { handleApiUnauthorized, switchProfile } from './auth/auth';
import { useCanSwitchProfile } from './hooks/useAllAuthProfiles';
import useUserStore from './store/user';

const EntityOverview = React.lazy(() => import('./views/EntityOverview'));
const EntityGroupDetails = React.lazy(
  () => import('./views/EntityGroupDetails'),
);
const WorkOrderOverview = React.lazy(() => import('./views/WorkOrderOverview'));
const WorkOrderDetails = React.lazy(() => import('./views/WorkOrderDetails'));

const Dashboard = React.lazy(() => import('./views/Dashboard'));
const NotFound = React.lazy(() => import('./views/NotFound'));

defineMessage({
  id: 'eq',
  defaultMessage: 'Equals',
});

const messages = defineMessages({
  splashNewUser: {
    id: 'splash.new_user_instructions',
    defaultMessage:
      "You are seeing this page because the organization that invited you has not yet assigned you any privileges in this system. Please contact them, and let them know that you've successfully signed up.",
  },
  splashNotAdmin: {
    id: 'splash.not_admin',
    defaultMessage:
      'This web application is only meant for administrators. If you are a contractor agent, use the HållKoll contractor web application instead.',
  },
});

const NotAdminSplash: FC<{ message: string }> = ({ message }) => {
  const canSwitchProfile = useCanSwitchProfile();
  return (
    <SplashMessage
      message={message}
      onSwitchProfile={canSwitchProfile ? switchProfile : undefined}
    />
  );
};

const Router: FC = () => {
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const intl = useIntl();
  const tenant = useUserStore((state) => state.tenant);
  const { dashboardEnabled } = getTenantFeatures(tenant);

  useEffect(() => {
    const onError = (error: unknown): void => {
      // A 401 with a time-valid token means the profile was deactivated
      // mid-session — drop the cached token so the next call re-mints.
      if ((error as AxiosError).response?.status === 401) {
        handleApiUnauthorized();
      }
      // The unmatched-entities 422 from POST /workorders?require_match=true
      // is explained inline by the create work order dialog; no snackbar on
      // top of it.
      if (isRoadAuthorityUnmatchedError(error)) {
        return;
      }
      enqueueSnackbar(
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
        (
          (error as AxiosError).response?.data as
            | { message: string }
            | undefined
        )?.message || (error as Error).message,
        { variant: 'error' },
      );
    };
    queryClient.setDefaultOptions({
      queries: { onError },
      mutations: { onError },
    });
  }, [enqueueSnackbar, queryClient]);

  return (
    <Routes>
      <Route
        path="/*"
        element={
          <RequireLogin>
            <FeatureLicenseProvider>
              <UserProfile>
                <Permissions
                  hasAny={true}
                  fallback={
                    <SplashMessage
                      message={intl.formatMessage(messages.splashNewUser)}
                    />
                  }
                >
                  <Permissions
                    oneOf={[
                      'role:admin',
                      'role:viewer',
                      'role:contractor-admin',
                    ]}
                    fallback={
                      <NotAdminSplash
                        message={intl.formatMessage(messages.splashNotAdmin)}
                      />
                    }
                  >
                    <EditWorkOrderDialog />
                    <SearchResults />
                    <Guide />
                    <CheckInPlanManagementDialog />
                    <RecurringWOPlanManagementDialog />
                    <SchemaManagementDialog />
                    <WorkOrderInstructionManager />
                    <TagManagementDialog />
                    <ContractorManagementDialog />
                    <div className="flex h-full flex-col overflow-hidden">
                      <TopBar />
                      <Suspense fallback={<LoadingScreen />}>
                        <Outlet />
                      </Suspense>
                    </div>
                    <FetchIndicator />
                  </Permissions>
                </Permissions>
              </UserProfile>
            </FeatureLicenseProvider>
          </RequireLogin>
        }
      >
        <Route
          path={RouteDefs.workOrderDetails.substring(1)}
          element={<WorkOrderDetails />}
        />
        <Route
          path={RouteDefs.stationDetails.substring(1)}
          element={<EntityGroupDetails />}
        />
        <Route
          path={RouteDefs.workOrderOverviewState.substring(1)}
          element={<WorkOrderOverview />}
        />
        <Route
          path={RouteDefs.workOrderOverview.substring(1)}
          element={<WorkOrderOverview />}
        />
        <Route
          path={RouteDefs.stationOverview.substring(1)}
          element={<EntityOverview />}
        />
        {dashboardEnabled && (
          <Route
            path={RouteDefs.dashboard.substring(1)}
            element={<Dashboard />}
          />
        )}
        <Route
          index
          element={
            <Navigate
              to={
                dashboardEnabled
                  ? RouteDefs.dashboard
                  : RouteDefs.stationOverview
              }
            />
          }
        />
        <Route path="*" element={<NotFound />} />
      </Route>
    </Routes>
  );
};

export default Router;
